<template>
  <div class="ai-generate-modal-wrapper">
    <Modal :value="value" @input="(v) => $emit('input', v)" :title="modalTitle" width="1000px" :mask-closable="false"
      :closable="!generating" class-name="ai-generate-modal">
      <div class="ai-gen-modal-content">
        <div class="ai-gen-tabs">
          <button v-for="item in generateModeOptions" :key="item.value" type="button"
            :class="{ active: generateMode === item.value }" :disabled="generating" @click="generateMode = item.value">
            <span>{{ item.label }}</span>
            <small>{{ item.description }}</small>
          </button>
        </div>

        <div class="ai-gen-modal-body">
          <aiGenerateSingleModal ref="singleModal" v-show="generateMode === 'single'" :dimension="dimension"
            :multi-lang-image-data="multiLangImageData" :sku-image-set-list="skuImageSetList"
            :filenode-view-target-url="filenodeViewTargetUrl" :generating="generating" />
          <aiGenerateFusionModal ref="fusionModal" v-show="generateMode === 'fusion'" :dimension="dimension"
            :multi-lang-image-data="multiLangImageData" :sku-image-set-list="skuImageSetList"
            :filenode-view-target-url="filenodeViewTargetUrl" :generating="generating" />
          <div class="ai-gen-section">
            <div class="ai-gen-section-title">
              保存目标配置
              <span class="ai-gen-hint">（生成后直接存入对应{{ dimension === "sku" ? "图套" : "语言" }}）</span>
            </div>
            <div class="ai-gen-target-box">
              <div class="ai-gen-target-row">
                <div class="ai-gen-target-col">
                  <label class="ai-gen-target-label"> <span class="required-star">*</span>选择存储语言 </label>
                  <Select :value="targetConfig.saveLanguage" :disabled="generating" transfer
                    @on-change="onLanguageChange">
                    <Option v-for="lang in langOptions" :key="lang.value" :value="lang.value">
                      {{ lang.label }}
                    </Option>
                  </Select>
                </div>
                <div v-if="dimension === 'sku'" class="ai-gen-target-col">
                  <label class="ai-gen-target-label"> <span class="required-star">*</span>选择存储图套 </label>
                  <Select :value="targetConfig.imageSetId" :disabled="generating" transfer
                    :placeholder="currentSetOptions.length === 0 ? '当前语言下暂无图套' : '请选择图套'"
                    @on-change="onTargetImageSetChange">
                    <Option v-for="item in currentSetOptions" :key="item.productGoodsImageSetId"
                      :value="item.productGoodsImageSetId">
                      {{ item.setName }} ({{ getSetTypeLabel(item.setType) }})
                    </Option>
                    <Option v-if="!generating" value="__add_new__">+ 添加图套</Option>
                  </Select>
                </div>
              </div>

              <div class="ai-gen-target-row mt10">
                <div class="ai-gen-target-col">
                  <label class="ai-gen-target-label"> <span class="required-star">*</span>生成图片尺寸 </label>
                  <Select :value="targetConfig.size" :disabled="generating" transfer
                    @on-change="setTargetConfigValue('size', $event)">
                    <Option v-for="item in imageSizeOptions" :key="item" :value="item">{{ item }}</Option>
                  </Select>
                </div>
                <div class="ai-gen-target-col">
                  <label class="ai-gen-target-label"> <span class="required-star">*</span>图片分辨率 </label>
                  <Select :value="targetConfig.resolution" :disabled="generating" transfer
                    @on-change="setTargetConfigValue('resolution', $event)">
                    <Option v-for="item in imageResolutionOptions" :key="item" :value="item">{{ item }}</Option>
                  </Select>
                </div>
              </div>

              <div v-if="dimension === 'sku'" class="ai-gen-sync-row">
                <Checkbox :value="targetConfig.syncSameModel" :disabled="generating"
                  @on-change="setTargetConfigValue('syncSameModel', $event)"> 同步存储至相同 model 的 SKU </Checkbox>
                <Checkbox :value="targetConfig.syncSameColor" :disabled="generating" style="margin-left: 16px"
                  @on-change="setTargetConfigValue('syncSameColor', $event)">
                  同步存储至相同 color 的 SKU
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <Button @click="handleCancel" :disabled="generating">取 消</Button>
        <Button type="primary" @click="handleGenerate" :loading="generating">
          {{ generating ? "处理中..." : "开始处理并保存" }}
        </Button>
      </div>
    </Modal>

    <imageSetFormModal ref="imageSetFormModal" @on-confirm="handleImageSetFormConfirm" />
  </div>
</template>

<script>
import api from "@/api/api";
import aiGenerateSingleModal from "./aiGenerateSingleModal";
import aiGenerateFusionModal from "./aiGenerateFusionModal";
import imageSetFormModal from "./imageSetFormModal";

export default {
  name: "aiGenerateModal",
  components: {
    aiGenerateSingleModal,
    aiGenerateFusionModal,
    imageSetFormModal,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dimension: {
      type: String,
      default: "spu",
      validator: (v) => ["spu", "sku"].includes(v),
    },
    productId: {
      type: String,
      default: "",
    },
    productGoodsId: {
      type: [String, Number],
      default: "",
    },
    multiLangImageData: {
      type: Array,
      default: () => [],
    },
    skuImageSetList: {
      type: Array,
      default: () => [],
    },
    filenodeViewTargetUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      generateMode: "single",
      generating: false,
      targetConfig: {
        saveLanguage: "common",
        imageSetId: "",
        lastValidSetId: "",
        syncSameModel: false,
        syncSameColor: false,
        size: "1:1",
        resolution: "1k",
      },
      imageSizeOptions: ["1:1", "3:2", "2:3", "4:3", "3:4", "5:4", "4:5", "16:9", "9:16", "2:1", "1:2", "21:9", "9:21"],
      imageResolutionOptions: ["1k", "2k", "4k"],
      generateModeOptions: [
        {
          value: "single",
          label: "一对一生成",
          description: "可一次选多张图，每张图各配一个提示词，分别生成",
        },
        {
          value: "fusion",
          label: "多图合成",
          description: "多张图共用一个提示词，合成一张",
        },
      ],
    };
  },
  computed: {
    modalTitle() {
      return this.dimension === "spu" ? "AI生图 —— SPU维度" : "AI生图 —— SKU维度";
    },
    langOptions() {
      return [
        { value: "common", label: "通用" },
        { value: "en", label: "英语 (EN)" },
        { value: "fr", label: "法语 (FR)" },
        { value: "de", label: "德语 (DE)" },
        { value: "ru", label: "俄语 (RU)" },
        { value: "nl", label: "荷兰语 (NL)" },
      ];
    },
    currentSetOptions() {
      if (this.dimension !== "sku") return [];
      return (this.skuImageSetList || []).filter((item) => item.language === this.targetConfig.saveLanguage);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.resetForm();
          this.$nextTick(() => {
            this.$refs.singleModal && this.$refs.singleModal.resetForm();
            this.$refs.fusionModal && this.$refs.fusionModal.resetForm();
          });
        } else {
          this.generating = false;
        }
      },
    },
  },
  methods: {
    createTargetConfig() {
      const config = {
        saveLanguage: "common",
        imageSetId: "",
        lastValidSetId: "",
        syncSameModel: false,
        syncSameColor: false,
        size: "1:1",
        resolution: "1k",
      };
      if (this.dimension === "sku") {
        const firstSet = (this.skuImageSetList || []).find((item) => item.language === config.saveLanguage);
        config.imageSetId = firstSet ? firstSet.productGoodsImageSetId : "";
        config.lastValidSetId = config.imageSetId;
      }
      return config;
    },
    resetForm() {
      this.generateMode = "single";
      this.targetConfig = this.createTargetConfig();
      this.generating = false;
    },
    setTargetConfigValue(field, value) {
      this.$set(this.targetConfig, field, value);
    },
    onLanguageChange(language) {
      this.$set(this.targetConfig, "saveLanguage", language);
      if (this.dimension !== "sku") return;
      const firstSet = (this.skuImageSetList || []).find((item) => item.language === language);
      const imageSetId = firstSet ? firstSet.productGoodsImageSetId : "";
      this.$set(this.targetConfig, "imageSetId", imageSetId);
      this.$set(this.targetConfig, "lastValidSetId", imageSetId);
    },
    onTargetImageSetChange(imageSetId) {
      const config = this.targetConfig;
      this.$set(config, "imageSetId", imageSetId);
      if (imageSetId === "__add_new__") {
        this.$refs.imageSetFormModal.open("add");
        this.$nextTick(() => {
          this.$set(config, "imageSetId", config.lastValidSetId || "");
        });
      } else {
        this.$set(config, "lastValidSetId", imageSetId);
      }
    },
    getSetTypeLabel(setType) {
      const map = { 1: "网图", 2: "非实拍自修", 3: "实拍自修" };
      return map[String(setType)] || String(setType || "");
    },
    handleImageSetFormConfirm({ setName, setType }) {
      const config = this.targetConfig;
      const language = config.saveLanguage;
      const duplicate = (this.skuImageSetList || []).find((item) => {
        return item.language === language && item.setName === setName;
      });
      if (duplicate) {
        this.$Message.warning(`图套名称已存在：${setName}`);
        this.$refs.imageSetFormModal.close();
        return;
      }
      const payload = {
        productGoodsId: this.productGoodsId,
        language,
        setName,
        setType,
      };
      this.axios.post(api.add_productGoodsImageSet, payload).then((res) => {
        if (res && res.data && res.data.code === 0) {
          this.$Message.success("新增图套成功");
          const imageSetId = res.data.datas || String(Date.now());
          this.$set(config, "imageSetId", imageSetId);
          this.$set(config, "lastValidSetId", imageSetId);
          this.$emit("on-image-set-added", {
            productGoodsImageSetId: imageSetId,
            setName,
            setType,
            language,
            productGoodsId: this.productGoodsId,
            images: [],
          });
        }
      });
      this.$refs.imageSetFormModal.close();
    },
    validateTargetConfig() {
      if (!this.targetConfig.saveLanguage) {
        this.$Message.error("请选择存储语言");
        return false;
      }
      if (!this.targetConfig.size) {
        this.$Message.error("请选择生成图片尺寸");
        return false;
      }
      if (!this.targetConfig.resolution) {
        this.$Message.error("请选择图片分辨率");
        return false;
      }
      if (this.dimension === "sku") {
        if (!this.productGoodsId) {
          this.$Message.error("缺少 SKU 标识，请先保存 SKU");
          return false;
        }
        if (!this.targetConfig.imageSetId) {
          this.$Message.error("请选择存储图套");
          return false;
        }
      }
      return true;
    },
    getSyncTo() {
      const syncTo = [];
      if (this.targetConfig.syncSameModel) syncTo.push(1);
      if (this.targetConfig.syncSameColor) syncTo.push(2);
      return syncTo;
    },
    buildModePayload(formData, type) {
      const payload = Object.assign({}, formData, {
        productId: this.productId,
        saveLanguage: this.targetConfig.saveLanguage,
        size: this.targetConfig.size,
        resolution: this.targetConfig.resolution,
        syncTo: this.getSyncTo(),
      });
      if (!this.$common.isEmpty(this.targetConfig.imageSetId)) {
        payload.imageSetId = this.targetConfig.imageSetId;
      }
      if (!this.$common.isEmpty(this.productGoodsId)) {
        payload.productGoodsId = this.productGoodsId;
      }
      payload.generateMode = type;
      return payload;
    },
    handleGenerate() {
      if (this.generating) return;
      const generateMode = this.generateMode;
      let [refMdal, formValid, payload, modeValue] = [null, false, {}, null];
      if (generateMode == "fusion") {
        refMdal = this.$refs.fusionModal;
        formValid = refMdal.validate();
        modeValue = 2;
      } else {
        refMdal = this.$refs.singleModal;
        formValid = refMdal.validate();
        modeValue = 1;
      }
      if (!refMdal || !formValid) return;
      const targetValid = this.validateTargetConfig();
      if (!targetValid) return;
      payload = this.buildModePayload(refMdal.getFormData(), modeValue);
     
      this.generating = true;
      this.axios.post(api.productAiGenerateImage, payload).then((res) => {
        if (res && res.data && res.data.code === 0) {
          this.$Message.success("已提交，异步处理中...");
          this.$emit("on-generate", { generateMode, data: payload });
          this.$emit("input", false);
        } else {
          const errorMsg = (res && res.data && res.data.message) || "操作失败";
          this.$Message.error(errorMsg);
        }
      }).catch((err) => {
        this.$Message.error(typeof err === "string" ? err : (err && err.message) || "生图失败，请重试");
      }).finally(() => {
        this.generating = false;
      });

      // let singleData = this.buildModePayload(singleModal.getFormData(), "1");
      // let fusionData = this.buildModePayload(fusionModal.getFormData(), "2");
      // // console.log(singleData, "singleData");
      // // console.log(fusionData, "fusionData");
      // let promiseList = [this.axios.post(api.productAiGenerateImage, singleData), this.axios.post(api.productAiGenerateImage, fusionData)];
      // this.generating = true;
      // Promise.all(promiseList)
      //   .then((resArr) => {
      //     const [singleRes, fusionRes] = resArr;
      //     if (singleRes && singleRes.data && singleRes.data.code === 0 && fusionRes && fusionRes.data && fusionRes.data.code === 0) {
      //       this.$Message.success("已提交，异步处理中...");
      //       this.$emit("on-generate", { single: singleData, fusion: fusionData }, { single: singleRes, fusion: fusionRes });
      //       this.$emit("input", false);
      //     } else {
      //       const errorMsg = (singleRes && singleRes.data && singleRes.data.message) || (fusionRes && fusionRes.data && fusionRes.data.message) || "操作失败";
      //       this.$Message.error(errorMsg);
      //     }
      //   })
      //   .catch((err) => {
      //     this.$Message.error(typeof err === "string" ? err : (err && err.message) || "生图失败，请重试");
      //   })
      //   .finally(() => {
      //     this.generating = false;
      //   });
    },
    handleCancel() {
      this.$emit("input", false);
    },
  },
};
</script>

<style lang="less">
.ai-generate-modal {
  .ivu-modal-body {
    height: calc(100vh - 250px) !important;
    overflow: hidden;
  }

  .ai-gen-tabs {
    display: flex;
    gap: 10px;
    margin: -8px 0 12px;
    border-bottom: 1px solid #e8eaec;

    button {
      flex: 1;
      padding: 8px 10px;
      border: 1px solid #dcdee2;
      border-bottom: 0;
      border-radius: 4px 4px 0 0;
      background: #f8f8f9;
      color: #515a6e;
      cursor: pointer;

      &.active {
        position: relative;
        color: #2d8cf0;
        background: #fff;

        &:after {
          content: "";
          position: absolute;
          right: 0;
          bottom: -1px;
          left: 0;
          height: 3px;
          background: #2d8cf0;
        }
      }

      &:disabled {
        cursor: not-allowed;
      }

      span,
      small {
        display: block;
      }

      span {
        font-size: 14px;
        font-weight: 600;
      }

      small {
        margin-top: 3px;
        color: #999;
        font-size: 12px;
      }
    }
  }

  .ai-gen-modal-content {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .ai-gen-modal-body {
    flex: 1;
    overflow: auto;
    padding: 0 10px;

    &::-webkit-scrollbar {
      width: 4px;
    }
  }

  .ai-gen-section {
    margin-bottom: 20px;
  }

  .ai-gen-section-title {
    margin-bottom: 10px;
    color: #17233d;
    font-size: 14px;
    font-weight: 600;

    .required-star {
      margin-right: 4px;
      color: #ed4014;
    }

    .ai-gen-hint {
      margin-left: 4px;
      color: #999;
      font-size: 12px;
      font-weight: normal;
    }
  }

  .ai-gen-target-box {
    padding: 16px;
    border-radius: 4px;
    background: #f5f7fa;

    .ai-gen-target-row {
      display: flex;
      gap: 16px;

      .ai-gen-target-col {
        flex: 1;
        min-width: 0;
      }
    }

    .ai-gen-target-label {
      display: block;
      margin-bottom: 6px;
      color: #515a6e;
      font-size: 13px;

      .required-star {
        margin-right: 4px;
        color: #ed4014;
      }
    }

    .ai-gen-sync-row {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px dashed #dcdee2;
    }
  }
}
</style>
